import * as React from 'react';
import { useState } from 'react';
import { Layout, BackTop, Tooltip } from 'antd';
import {
	FileDoneOutlined,
	ToolOutlined,
	DollarOutlined,
	ProfileOutlined,
	QuestionCircleOutlined,
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import moment from 'moment';
import useMountEffect from '../../hooks/useMountEffect';
import ScrollToTopOnMount from '../scroll_to_top_on_mount/ScrollToTopOnMount';
import {
	getInternalTeamId,
	getInternalTeamName,
	getInternalTeamRecords,
	internalTeamsThunkForRole,
	INTERNAL_TEAMS_PAGINATION,
	INTERNAL_TEAMS_TC,
	INTERNAL_TEAM_FILTERS,
} from '../internal_team_select/internalTeams';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	getCurrency,
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import OWResult from '../OWResult';
import ProjectAccessModal from '../project_access_modal/ProjectAccessModal';
import { TextButton } from '../text_button/TextButton';
import { OWStatCard } from '../ow_stat_card/OWStatCard';
import BudgetVsDeliverablesChart from '../charts/BudgetVsDeliverablesChart';
import { getProjectTemplateFieldEntries } from '../../utils/ProjectTemplateUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import {
	projectsRestCrudThunksForBuyer,
	projectsRestCrudThunksForSupplier,
} from '../../thunks/projects_thunks';
import {
	deliverablesRestCrudThunksForBuyer,
	deliverablesRestCrudThunksForSupplier,
} from '../../thunks/deliverables_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { PROJECT_ENTITY_TYPES } from '../../constants/ProjectEntities';
import { cn } from '../../utils/ClassNamesUtils';
import { OscarAskInput } from '../../ai_agent';

const OVERVIEW_DELIVERABLES_TC = 'PROJECT_OVERVIEW_DELIVERABLES';

// Spelled out so Tailwind's scanner sees each class.
const STAT_GRID_COLUMNS = {
	1: 'grid-cols-1',
	2: 'grid-cols-2',
	3: 'grid-cols-3',
};

const { Content } = Layout;

// A muted-below-value meta row (Figma right sidebar). Renders nothing when the
// value is empty so unsupported fields (teams, budget type, entities) simply
// don't appear.
//
// valueClassName lets a caller render a value that is deliberately not a value — a
// template field with nothing saved — without it reading as real data.
const MetaRow = ({ value, label, valueClassName }: any) =>
	value ? (
		<div className="border-0 border-b border-solid border-grey-4 py-3 last:border-b-0">
			<div className={valueClassName || 'bodyregular whitespace-pre-line text-character-primary'}>
				{value}
			</div>
			<div className="footnoteregular text-character-secondary">{label}</div>
		</div>
	) : null;

// Who can see the project. Unlike MetaRow this always renders: "no teams picked"
// is a real answer (every internal team can see it), and hiding the row was why
// nobody could tell how project access is set. Buyers get a Manage affordance —
// internal teams are the buyer's own private suppliers, so only a buyer can scope
// a project to them, matching ProjectEditForm.
const AccessRow = ({ value, canManage, onManage }: any) => (
	<div className="border-0 border-b border-solid border-grey-4 py-3 last:border-b-0">
		<div className="bodyregular whitespace-pre-line text-character-primary">{value}</div>
		<div className="footnoteregular flex items-center gap-2 text-character-secondary">
			<span>Who can access</span>
			<Tooltip title="The internal teams picked here see this project along with its deliverables, tasks and notes. With none picked, every internal team can see it.">
				<QuestionCircleOutlined />
			</Tooltip>
			{canManage ? <TextButton onClick={onManage}>Manage</TextButton> : null}
		</div>
	</div>
);

// A stat card with a left summary (icon + label + big total) and a divided
// breakdown column — shared shape for the Deliverables and Work Orders cards.
const BreakdownCard = ({ icon, label, total, rows }: any) => (
	<OWStatCard className="flex items-center gap-3">
		<div className="flex flex-col gap-2">
			<div className="flex items-center gap-1">
				{icon}
				<span className="bodyregular text-character-secondary">{label}</span>
			</div>
			<span className="h4medium text-character-primary">{total}</span>
		</div>
		{rows ? (
			<div className="flex flex-1 flex-col border-0 border-l border-solid border-grey-5 pl-3">
				{rows.map((row) => (
					<div key={row.label} className="flex items-start gap-1">
						<span className="footnoteregular flex-1 text-character-secondary">{row.label}</span>
						<span className="footnoteMedium text-character-primary">{row.value}</span>
					</div>
				))}
			</div>
		) : null}
	</OWStatCard>
);

const ProjectsDetailOverviewV2Page = ({
	currentUser,
	project,
	deliverables,
	internalTeams,
	getProject,
	fetchDeliverables,
	fetchInternalTeams,
	match,
	history,
}) => {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [managingAccess, setManagingAccess] = useState(false);

	useMountEffect(() => {
		const projectId = match.params.id;
		const userType = nullSafeGetOrElse('userType', currentUser, 'buyer');
		getProject(userType, projectId)
			.then((record) => {
				setLoading(false);
				// The project carries only supplierFacilityIds, so fetch the internal teams
				// to name them — and only for a project that is actually scoped to some.
				if (nullSafeGetOrElse('supplierFacilityIds', record, []).length) {
					fetchInternalTeams(userType);
				}
			})
			.catch((err) => {
				setLoading(false);
				setError(err);
			});
		fetchDeliverables(projectId);
	});

	if (error) {
		return (
			<OWResult
				error={error}
				title="You no longer have access to this project."
				subTitle="Either its deleted or been reassigned."
			/>
		);
	}
	if (loading) {
		return <PageLoadingPlaceholder />;
	}

	const currency = getCurrency({ currentUser });
	const fmt = (amount) =>
		currency ? currency.symbolFormat(amount, 0) : `$${(Number(amount) || 0).toLocaleString()}`;

	// Only the entities the project was set up with get a card. A project saved before
	// entities existed has none recorded, so it keeps every card.
	const entities = nullSafeGetOrElse('entities', project, []);
	const tracksEntity = (entityType) => !entities.length || entities.includes(entityType);

	// Deliverables rollup (Not Started / In Progress / Completed).
	const deliverableMetrics = nullSafeGet('deliverableProjectMetrics', project);
	const deliverableRows = deliverableMetrics
		? [
				{
					label: 'Not Started',
					value: nullSafeGetOrElse('deliverablesNotStarted', deliverableMetrics, 0),
				},
				{
					label: 'In Progress',
					value: nullSafeGetOrElse('deliverablesInProgress', deliverableMetrics, 0),
				},
				{
					label: 'Completed',
					value: nullSafeGetOrElse('deliverablesCompleted', deliverableMetrics, 0),
				},
		  ]
		: null;
	const deliverablesTotal = deliverableRows
		? deliverableRows.reduce((sum, row) => sum + row.value, 0)
		: 0;

	// Work-order rollup. No "Paid" bucket at project level yet; In Progress folds
	// in on-hold, matching the existing summary.
	const woMetrics = nullSafeGet('workOrderProjectMetrics', project);
	const woRows = woMetrics
		? [
				{
					label: 'Not Started',
					value: nullSafeGetOrElse('workOrdersNotStarted', woMetrics, 0),
				},
				{
					label: 'In Progress',
					value:
						nullSafeGetOrElse('workOrdersInProgress', woMetrics, 0) +
						nullSafeGetOrElse('workOrdersOnHold', woMetrics, 0),
				},
				{ label: 'Completed', value: nullSafeGetOrElse('workOrdersClosed', woMetrics, 0) },
		  ]
		: null;
	const woTotal = woRows ? woRows.reduce((sum, row) => sum + row.value, 0) : 0;

	const taskMetrics = nullSafeGet('taskProjectMetrics', project);
	const tasksTotal = nullSafeGetOrElse('tasksTotal', taskMetrics, 0);
	// Deliverable and task cards also drop out at zero; the cards left behind share the
	// row between them.
	const showDeliverables = tracksEntity(PROJECT_ENTITY_TYPES.deliverables) && deliverablesTotal > 0;
	const showWorkOrders = tracksEntity(PROJECT_ENTITY_TYPES.workOrders);
	const showTasks = tracksEntity(PROJECT_ENTITY_TYPES.tasks) && tasksTotal > 0;
	// Budget always shows. Four cards are too many for one row beside the sidebar, so
	// they go 2×2; any other count fits on one row.
	const statCardCount = [showDeliverables, showWorkOrders, true, showTasks].filter(Boolean).length;
	const statCardColumns = statCardCount === 4 ? 2 : statCardCount;
	const budget = nullSafeGet('budget', project);
	const hasBudget = budget != null;
	// "Used" = approved invoice spend (same source as the old Budget widget).
	const budgetUsed = Number(
		nullSafeGetOrElse('invoiceProjectMetrics.totalInvoicesAmountApproved', project, 0)
	);
	const budgetPct =
		hasBudget && Number(budget) > 0 ? Math.round((budgetUsed * 100) / Number(budget)) : 0;

	const startDate = nullSafeGet('startDate', project);
	const endDate = nullSafeGetOrElse('endDate', project, nullSafeGet('dueDate', project));
	// A project may carry just one of the two dates, so the row says which date it has
	// rather than padding the missing half with a dash and leaving the reader to guess
	// which end is which.
	const formatDate = (date) => moment(date).format('MMM D, YYYY');
	const dateRange =
		startDate && endDate
			? `${formatDate(startDate)} - ${formatDate(endDate)}`
			: startDate
			? formatDate(startDate)
			: endDate
			? formatDate(endDate)
			: '';
	const dateRangeLabel =
		startDate && endDate ? 'Start Date - End Date' : startDate ? 'Start Date' : 'End Date';
	const templateName = nullSafeGet('projectTemplate.name', project);
	// The template is hydrated on the project itself, so its field definitions arrive
	// with the record — no separate template fetch to sequence against this render.
	const templateFieldEntries = getProjectTemplateFieldEntries(
		nullSafeGet('projectTemplate', project),
		nullSafeGetOrElse('projectTemplateDetails', project, [])
	);
	const description = nullSafeGet('description', project);
	// Which internal teams can see this project. An empty list means every internal
	// team can, which is how projects behaved before the field existed. The project
	// carries ids only, so names come from the internal teams fetched above — until
	// they arrive, show the count rather than a row full of ids.
	const scopedTeamIds = nullSafeGetOrElse('supplierFacilityIds', project, []);
	const scopedTeamNames = internalTeams
		.filter((team) => scopedTeamIds.includes(getInternalTeamId(team)))
		.map(getInternalTeamName);
	const internalTeamsValue = !scopedTeamIds.length
		? 'All internal teams'
		: scopedTeamNames.length
		? scopedTeamNames.join('\n')
		: `${scopedTeamIds.length} internal team${scopedTeamIds.length === 1 ? '' : 's'}`;

	return (
		<Content className="ProjectsDetailOverviewPage bg-white" style={{ padding: '16px' }}>
			<ScrollToTopOnMount />
			<BackTop />
			{/* Two columns: the stats and chart on the left, and Ask + project details pinned
			    to the top right, so they sit in the same place however many cards show. */}
			<div className="flex flex-wrap items-start gap-m">
				<div className="flex min-w-0 flex-1 flex-col gap-m">
					<div className={cn('grid gap-s', STAT_GRID_COLUMNS[statCardColumns])}>
						{showDeliverables ? (
							<BreakdownCard
								icon={<FileDoneOutlined className="text-[12px] text-character-secondary" />}
								label="Deliverables"
								total={deliverablesTotal}
								rows={deliverableRows}
							/>
						) : null}
						{showWorkOrders ? (
							<BreakdownCard
								icon={<ToolOutlined className="text-[12px] text-character-secondary" />}
								label="Work Orders"
								total={woTotal}
								rows={woRows}
							/>
						) : null}
						<OWStatCard className="flex flex-col justify-center gap-2">
							<div className="flex items-center gap-1">
								<DollarOutlined className="text-[12px] text-character-secondary" />
								<span className="bodyregular text-character-secondary">Budget</span>
								{hasBudget ? (
									<>
										<span className="bodyregular text-character-secondary">·</span>
										<span className="footnoteMedium text-character-primary">{budgetPct}% Used</span>
									</>
								) : null}
							</div>
							<span className="h4medium text-character-primary">
								{hasBudget ? `${fmt(budgetUsed)} / ${fmt(budget)}` : '—'}
							</span>
						</OWStatCard>
						{showTasks ? (
							<OWStatCard className="flex flex-col justify-center gap-2">
								<div className="flex items-center gap-1">
									<ProfileOutlined className="text-[12px] text-character-secondary" />
									<span className="bodyregular text-character-secondary">Tasks</span>
								</div>
								<span className="h4medium text-character-primary">
									{`${nullSafeGetOrElse('tasksCompleted', taskMetrics, 0)}/${tasksTotal}`}
								</span>
							</OWStatCard>
						) : null}
					</div>
					{/* overflow-hidden so a chart that mis-measures its container clips at the
					    card instead of drawing across the meta sidebar beside it. */}
					{showDeliverables ? (
						<div className="min-w-0 overflow-hidden rounded-[2px] border border-solid border-grey-5 p-4">
							<div className="h5medium mb-2 text-character-primary">
								Budget vs Deliverables Completed
							</div>
							<BudgetVsDeliverablesChart
								deliverables={deliverables}
								projectBudget={budget}
								currency={currency}
							/>
						</div>
					) : null}
				</div>
				<div className="flex w-[320px] shrink-0 flex-col">
					<OscarAskInput placeholder="Ask me anything about this project" className="mb-m" />
					<div className="rounded-[2px] border border-solid border-grey-5 px-4 py-1">
						<MetaRow value={description} label="Scope" />
						<MetaRow value={dateRange} label={dateRangeLabel} />
						<MetaRow value={templateName} label="Project Template" />
						{/* The template's own fields, in the same rows as everything else about the
					    project. Read-only here — they are edited on the project edit form, which
					    is where every other field on this sidebar is edited too. */}
						{templateFieldEntries.map((entry) => (
							<MetaRow
								key={`${entry.group}-${entry.field.key}`}
								value={entry.displayValue || 'Not set'}
								label={entry.isRequired ? entry.field.key : `${entry.field.key} (optional)`}
								valueClassName={
									entry.displayValue
										? undefined
										: // A required field with nothing saved is a gap worth seeing; an empty
										  // optional one is just empty.
										  `bodyregular ${
												entry.isRequired ? 'text-sunsetOrange-8' : 'text-character-secondary'
										  }`
								}
							/>
						))}
						<AccessRow
							value={internalTeamsValue}
							canManage
							onManage={() => setManagingAccess(true)}
						/>
					</div>
				</div>
			</div>

			{managingAccess ? (
				<ProjectAccessModal
					project={project}
					onCancel={() => setManagingAccess(false)}
					// The update writes the project back into the slice, which is where this
					// page reads it from, so the row re-renders without a refetch. A user who
					// removed their own team can no longer read the project, so leave the page.
					onSuccess={(record, lostAccess) => {
						setManagingAccess(false);
						if (lostAccess) {
							history.push(`/${ROLE_TYPES.SUPPLIER}/projects/overview/active`);
						}
					}}
				/>
			) : null}
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	project: getEntityById(nullSafeGet('match.params.id', ownProps), state.projects),
	fetching: state.projects.fetching,
	currentUser: state.session.currentUser,
	deliverables: getRecordsForTargetCollection(state.deliverables, OVERVIEW_DELIVERABLES_TC),
	internalTeams: getInternalTeamRecords(state.supplier_private_networks),
	match: ownProps.match,
	history: ownProps.history,
});

const mapDispatchToProps = (dispatch) => ({
	getProject: (userType, id) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectsRestCrudThunksForSupplier.readOne(id)
				: projectsRestCrudThunksForBuyer.readOne(id)
		),
	fetchInternalTeams: (userType) =>
		dispatch(
			internalTeamsThunkForRole(userType)(
				{},
				INTERNAL_TEAMS_TC,
				INTERNAL_TEAMS_PAGINATION,
				null,
				INTERNAL_TEAM_FILTERS
			)
		),
	fetchDeliverables: (projectId) =>
		dispatch((innerDispatch, getState) => {
			const userType = nullSafeGetOrElse('userType', getState().session.currentUser, 'buyer');
			const thunks =
				userType === ROLE_TYPES.SUPPLIER
					? deliverablesRestCrudThunksForSupplier
					: deliverablesRestCrudThunksForBuyer;
			return innerDispatch(
				thunks.read({ projectId }, OVERVIEW_DELIVERABLES_TC, { current: 1, pageSize: 200 })
			);
		}),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(ProjectsDetailOverviewV2Page)
);
